export interface ResearchArea {
    area: string
    description: string
    subarea: [string, string][],
    img?: string
    md?: string | (() => Promise<string | typeof import("*.md")>),
}

type AreaType = { [k: string]: ResearchArea }

function state() {
    const res_zh: AreaType = {
        hypergraph: {
            area: "超图计算",
            description: "",
            subarea: [
                ["超图计算理论", "研究超图基础数学理论，挖掘复杂关联背后的数学机理。"],
                ["超图结构优化", "研究超图结构建模与动态演化，实现高阶复杂关联的精确建模。"],
                ["超图神经网络", "研究超图神经网络模型，实现高阶关联引导的多模态数据协同表示学习方法。搭建超图神经网络框架（DeepHypergraph）。"]
            ],
            img: require("../assets/research area/hyper_icon.png"),
            md: () => import(/* webpackChunkName: "hypergraph" */ '../data/research-area/hypergraph.md'),
        },
        tools: {
            area: "超图大模型",
            description: "一个基于PyTorch的超图计算学习框架",
            subarea: [
                ["HyperExtract：高阶知识库生成",""],
                ["HyperRAG：高阶知识增强生成",""],
                ["超图基础模型",""]
            ],
            img: require('../assets/research area/hyper_llm.png'),
            md: () => import(/* webpackChunkName: "mining" */ '../data/research-area/tools.md'),
        },
        vision: {
            area: "视觉超图计算",
            description: "面向视觉信息中的复杂关联，实现多模态视觉融合感知与增强",
            subarea: [
                ["视觉目标检测", "建模图像、点云等视觉数据中的复杂关联，基于超图计算进行精确数据配准，突破开放场景中低重叠率、噪声数据影响下的配准初始化及精度受限难题。为视觉感知及3D检索、缺陷检测等提供技术支撑。"],
                ["视觉数据配准", "研究激光雷达、相机等多传感器融合的视觉场景高精度彩色点云重建。基于点云及图像的3D重建、搭建手持式及高精度多模态场景数据采集设备。研究深度数据质量评价方法，基于超图计算实现RGB-D质量主动评价，建立了首个xxx数据库。"],
                ["视觉场景理解", "基于事件数据的视频插帧、去遮挡、深度估计、低光等异常光照下复杂场景视觉数据增强及3D重建组织建立了面向xxx的xxx数据库、面向xxx的xxx数据库及面向xxx的xxx数据库。"]
            ],
            img: require("../assets/research area/3d_logo_invert.png"),
            md: () => import(/* webpackChunkName: "vision" */ '../data/research-area/vision.md'),
        },
        medicine: {
            area: "医学人工智能",
            description: "面向多模态医学信息中的复杂关联，实现医学辅助诊断、存活预测等任务",
            subarea: [
                ["脑疾病诊断", "建模功能脑网络、结构脑网络中的高阶关联，探索高阶关联视角下的功能-结构脑网络耦合关系，首先脑疾病辅助诊断、疾病严重程度预测等任务。"],
                ["病理图分析", "建模超大像素病理图中的多维度高阶关联，从拓扑、语义等空间实现基于病理图的存活预测、肿瘤类型诊断、分子分型等任务"],
                ["多模态融合", "基于影像学、病理图、实验室检验数据、音频等多模态信息，实现高阶关联视角下的多模态信息融合，为辅助诊断等任务提供更全面的信息"]
            ],
            img: require("../assets/research area/intro-medicine.png"),
            md: () => import(/* webpackChunkName: "vision2" */ '../data/research-area/medicine.md'),
        },
    }
    const res_en: AreaType = {
        hypergraph: {
            area: "Hypergraph Computation",
            description: "Foundational theories, structural analysis, representation learning, and foundation models for higher-order relations.",
            subarea: [
                ["Hypergraph Structure Parsing", "Discover, construct, and refine reliable higher-order structures from complex data."],
                ["Hypergraph Representation Fusion", "Fuse multi-view, multimodal, and cross-level relationships into unified higher-order representations."],
                ["Hypergraph Foundation Model", "Learn general-purpose higher-order relational representations through self-supervised pretraining and cross-domain transfer."]
            ],
            img: require("../assets/research area/hyper_logo.png"),
            md: () => import(/* webpackChunkName: "hypergraph" */ '../data/research-area/hypergraph_en.md'),
        },
        tools: {
            area: "Large Models",
            description: "Higher-order knowledge construction, knowledge-enhanced generation, and hypergraph foundation models for reliable large-model reasoning.",
            subarea: [
                ["Higher-Order Knowledge Base Generation", "Transform unstructured documents into reusable higher-order knowledge structures, including graphs and hypergraphs."],
                ["Higher-Order Knowledge-Enhanced Generation", "Use hypergraph-driven retrieval to ground large-model responses in complex domain knowledge and reduce hallucinations."],
                ["Hypergraph Foundation Model", "Learn, organize, and reason over higher-order relations through unified hypergraph foundation models."]
            ],
            img: require("../assets/research area/research_llm.svg"),
            md: () => import(/* webpackChunkName: "mining" */ '../data/research-area/tools_en.md'),
        },
        vision: {
            area: "Computer Vision",
            description: "Hypergraph-enhanced visual perception for complex scenes, spanning detection, counting, registration, and depth estimation.",
            subarea: [
                ["Object Detection", "Develop hypergraph-enhanced detectors that model higher-order correlations across multi-scale visual features."],
                ["Visual Data Registration", "Model higher-order correspondences across images, point clouds, and sensors for robust cross-modal alignment."],
                ["Visual Scene Understanding", "Jointly model spatial, temporal, and multi-source visual information for robust perception in complex scenes."]
            ],
            img: require("../assets/research area/research_vision.svg"),
            md: () => import(/* webpackChunkName: "vision" */ '../data/research-area/vision_en.md'),
        },
        medicine: {
            area: "Medical Intelligence",
            description: "Hypergraph-based modeling of complex relations in multimodal medical data for diagnosis, prognosis, and clinical decision support.",
            subarea: [
                ["Brain Disease Diagnosis", "Model higher-order relationships in functional and structural brain networks for disease diagnosis and severity prediction."],
                ["Pathology Graph Analysis", "Analyze multi-dimensional higher-order relations in gigapixel pathology images for survival prediction, tumor typing, and molecular subtyping."],
                ["Multimodal Fusion", "Fuse imaging, pathology, laboratory, and other clinical modalities through higher-order correlations for comprehensive assisted diagnosis."]
            ],
            img: require("../assets/research area/research_medicine.svg"),
            md: () => import(/* webpackChunkName: "vision2" */ '../data/research-area/medicine_en.md'),
        },
    }
    res_zh.hypergraph.subarea = [
        ["超图结构解析", "从复杂数据中发现、构建并优化可靠的高阶关联结构。"],
        ["超图融合表示", "融合多视图、多模态与跨层级关系，学习统一的高阶表示。"],
        ["超图基础模型", "通过自监督预训练学习通用高阶关系表征，支撑跨领域迁移。"],
    ]
    res_zh.hypergraph.img = require("../assets/research area/hyper_logo.png")
    res_zh.tools.area = "大模型"
    res_zh.tools.subarea = [
        ["高阶知识库生成", ""],
        ["高阶知识增强生成", ""],
        ["超图基础模型", ""],
    ]
    res_zh.tools.img = require("../assets/research area/research_llm.svg")
    res_zh.vision.area = "计算机视觉"
    res_zh.vision.img = require("../assets/research area/research_vision.svg")
    res_zh.medicine.area = "智慧医疗"
    res_zh.medicine.img = require("../assets/research area/research_medicine.svg")

    res_en.hypergraph.description = "Foundational theories, structural analysis, representation learning, and foundation models for higher-order relations."
    res_en.hypergraph.img = require("../assets/research area/hyper_logo.png")
    res_en.hypergraph.subarea = [
        ["Hypergraph Structure Parsing", "Discover, construct, and refine reliable higher-order structures from complex data."],
        ["Hypergraph Representation Fusion", "Fuse multi-view, multimodal, and cross-level relationships into unified higher-order representations."],
        ["Hypergraph Foundation Model", "Learn general-purpose higher-order relational representations through self-supervised pretraining and cross-domain transfer."],
    ]

    res_en.tools.area = "Large Models"
    res_en.tools.description = "Higher-order knowledge construction, knowledge-enhanced generation, and hypergraph foundation models for reliable large-model reasoning."
    res_en.tools.img = require("../assets/research area/research_llm.svg")
    res_en.tools.subarea = [
        ["Higher-Order Knowledge Base Generation", "Transform unstructured documents into reusable higher-order knowledge structures, including graphs and hypergraphs."],
        ["Higher-Order Knowledge-Enhanced Generation", "Use hypergraph-driven retrieval to ground large-model responses in complex domain knowledge and reduce hallucinations."],
        ["Hypergraph Foundation Model", "Learn, organize, and reason over higher-order relations through unified hypergraph foundation models."],
    ]

    res_en.vision.area = "Computer Vision"
    res_en.vision.img = require("../assets/research area/research_vision.svg")
    res_en.vision.description = "Hypergraph-enhanced visual perception for complex scenes, spanning detection, counting, registration, and depth estimation."
    res_en.vision.subarea = [
        ["Object Detection", "Develop hypergraph-enhanced detectors that model higher-order correlations across multi-scale visual features."],
        ["Visual Data Registration", "Model higher-order correspondences across images, point clouds, and sensors for robust cross-modal alignment."],
        ["Visual Scene Understanding", "Jointly model spatial, temporal, and multi-source visual information for robust perception in complex scenes."],
    ]

    res_en.medicine.area = "Medical Intelligence"
    res_en.medicine.img = require("../assets/research area/research_medicine.svg")
    res_en.medicine.description = "Hypergraph-based modeling of complex relations in multimodal medical data for diagnosis, prognosis, and clinical decision support."
    res_en.medicine.subarea = [
        ["Brain Disease Diagnosis", "Model higher-order relationships in functional and structural brain networks for disease diagnosis and severity prediction."],
        ["Pathology Graph Analysis", "Analyze multi-dimensional higher-order relations in gigapixel pathology images for survival prediction, tumor typing, and molecular subtyping."],
        ["Multimodal Fusion", "Fuse imaging, pathology, laboratory, and other clinical modalities through higher-order correlations for comprehensive assisted diagnosis."],
    ]

    return { zh: res_zh, en: res_en }
}

export default state()
